/**
 * The last search the user opened, remembered for the sidebar's parameter-less
 * "Search Detail" entry.
 *
 * The sidebar item exists because the v38 prototype had a standalone Search
 * Detail screen, and dropping it from the nav would silently change the agreed
 * information architecture. But a detail page needs a subject, so the landing
 * behaviour is: send them back to the search they were last looking at, and
 * otherwise ask them to pick one.
 *
 * `localStorage` and not the URL: it has to survive a full page load (the user
 * clicks the sidebar item in a fresh tab), which rules out component state, and
 * it is a convenience rather than data — every read is wrapped because Safari's
 * private mode throws on access, and a nav item is not worth a crashed page.
 */

const KEY = "nu-assist:last-viewed-search";

/** Thrive search ids are numeric; anything else is a stale or tampered value. */
const VALID = /^[0-9]{1,20}$/;

export function rememberLastViewedSearch(searchId: string): void {
  if (!VALID.test(searchId)) return;
  try {
    window.localStorage.setItem(KEY, searchId);
  } catch {
    // Private browsing / disabled storage. A forgotten id is a non-event.
  }
}

export function readLastViewedSearch(): string | null {
  try {
    const value = window.localStorage.getItem(KEY);
    return value && VALID.test(value) ? value : null;
  } catch {
    return null;
  }
}
